import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO, { generateBreadcrumbSchema } from '@/components/SEO';
import { getBlogs } from '@/lib/api';
import { format } from 'date-fns';
import { ArrowUpRight, Search } from 'lucide-react';

const defaultBlogs = [
  {
    id: '1',
    title: 'Why most marketing fails (and how to engineer revenue instead)',
    slug: 'why-most-marketing-fails',
    excerpt: 'Attention without conversion is noise. Here\'s the playbook we use to turn ad spend into compounding pipeline.',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200',
    author: 'Wizbang Team',
    tags: ['Strategy', 'Growth'],
    created_at: new Date().toISOString(),
  },
  {
    id: '2',
    title: 'The 2026 performance advertising stack we actually ship',
    slug: 'performance-stack-2026',
    excerpt: 'PMAX + CAPI + creative testing loops + first-party data. The exact mix that\'s lifting ROAS 5x for our clients.',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200',
    author: 'Wizbang Team',
    tags: ['Ads', 'Performance'],
    created_at: new Date().toISOString(),
  },
  {
    id: '3',
    title: 'Branding for performance marketers (yes, you read that right)',
    slug: 'branding-for-performance-marketers',
    excerpt: 'Brand is what makes your ads cheaper. Here\'s why the smartest growth teams are quietly investing in identity.',
    image: 'https://images.unsplash.com/photo-1518186285589-2f7649de83e0?w=1200',
    author: 'Wizbang Team',
    tags: ['Brand', 'Automation'],
    created_at: new Date().toISOString(),
  },
];

export default function Blog() {
  const [blogs, setBlogs] = useState(defaultBlogs);
  const [query, setQuery] = useState('');
  const [activeTag, setActiveTag] = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await getBlogs();
      if (res.data && res.data.length > 0) setBlogs(res.data);
    } catch (_) {}
  };

  const allTags = Array.from(new Set(blogs.flatMap((b) => b.tags || [])));
  const filtered = blogs.filter((b) => {
    const q = query.toLowerCase();
    const matchesQ = !q || b.title.toLowerCase().includes(q) || b.excerpt.toLowerCase().includes(q);
    const matchesTag = !activeTag || (b.tags || []).includes(activeTag);
    return matchesQ && matchesTag;
  });

  return (
    <div className="min-h-screen" style={{ background: '#FAFAF7' }} data-testid="blog-page">
      <SEO
        title="Blog — Growth, Marketing & Technology Insights"
        description="The Wizbang journal: performance marketing playbooks, e-commerce growth tactics, web development case studies, branding deep-dives and creative strategy."
        keywords="digital marketing blog India, growth marketing insights, performance advertising tips, ecommerce growth, branding articles, SEO playbook"
        url="/blog"
        structuredData={generateBreadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Blog', path: '/blog' },
        ])}
      />
      <Navbar />

      <section className="pt-32 pb-12 relative">
        <div className="absolute inset-0 bg-gradient-radial opacity-60" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="eyebrow mb-6">The journal</div>
          <h1 className="font-syne text-5xl sm:text-6xl lg:text-7xl font-bold text-[#0A0A0A] mb-6 max-w-5xl tracking-tight leading-[0.95]">
            Field notes from the <span className="font-editorial text-[#ff5a1f]">studio.</span>
          </h1>
          <p className="text-[#5B5B5B] text-lg md:text-xl max-w-2xl leading-relaxed">
            Playbooks, post-mortems and points-of-view from the people running our campaigns, shipping our code and shaping our brands.
          </p>
        </div>
      </section>

      {/* Search & tag filters */}
      <section className="border-y bg-white sticky top-20 z-30" style={{ borderColor: 'var(--border)' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8A8A]" />
            <input data-testid="blog-search" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Search articles..."
              className="w-full pl-10 pr-4 py-2.5 border rounded-full text-sm text-[#0A0A0A] focus:outline-none focus:border-[#ff5a1f]"
              style={{ borderColor: 'var(--border)', background: '#FAFAF7' }} />
          </div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="blog-tag-all" onClick={() => setActiveTag('')}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${!activeTag ? 'bg-[#0A0A0A] text-white' : 'border text-[#0A0A0A] hover:border-[#ff5a1f]'}`}
              style={!activeTag ? {} : { borderColor: 'var(--border)' }}>All</button>
            {allTags.map((t) => (
              <button key={t} data-testid={`blog-tag-${t}`} onClick={() => setActiveTag(t)}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${activeTag === t ? 'bg-[#0A0A0A] text-white' : 'border text-[#0A0A0A] hover:border-[#ff5a1f]'}`}
                style={activeTag === t ? {} : { borderColor: 'var(--border)' }}>#{t}</button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16" data-testid="blog-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {filtered.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map((blog) => (
                <Link key={blog.id} to={`/blog/${blog.slug}`} className="group" data-testid={`blog-card-${blog.id}`}>
                  <article className="bg-white border rounded-2xl overflow-hidden hover:border-[#ff5a1f] transition-all duration-500 card-tilt"
                    style={{ borderColor: 'var(--border)' }}>
                    <div className="aspect-video overflow-hidden bg-[#F4F2EC]">
                      <img src={blog.image} alt={blog.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                    <div className="p-6">
                      <div className="flex gap-2 mb-3">
                        {blog.tags?.slice(0, 2).map((tag) => (
                          <span key={tag} className="text-xs font-mono text-[#ff5a1f] uppercase">#{tag}</span>
                        ))}
                      </div>
                      <h2 className="font-syne text-xl font-bold text-[#0A0A0A] mb-3 group-hover:text-[#ff5a1f] transition-colors line-clamp-2 tracking-tight">
                        {blog.title}
                      </h2>
                      <p className="text-[#5B5B5B] text-sm mb-4 line-clamp-2 leading-relaxed">{blog.excerpt}</p>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-[#8A8A8A]">{blog.created_at && format(new Date(blog.created_at), 'MMM d, yyyy')}</span>
                        <span className="text-[#0A0A0A] font-semibold flex items-center gap-1 group-hover:text-[#ff5a1f] transition-colors">
                          Read article <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-45" />
                        </span>
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-[#5B5B5B] text-lg mb-4">No articles match this filter.</p>
              <button onClick={() => { setQuery(''); setActiveTag(''); }} className="text-[#ff5a1f] font-semibold">Clear filters</button>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
